import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../lib/auth/AuthProvider';
import { needsPersonaOnboarding, shouldSkipOnboardingCheck, setOnboardingRedirectPath } from '../../../utils/onboardingUtils';
import { setInviteCodeRedirectPath } from '../../../utils/inviteCodeUtils';
import { useSetAtom } from 'jotai';
import { checkInviteCodeVerificationAtom } from '../../../atoms/v3/inviteCodeAtoms';

/**
 * v3版本的私有路由组件
 * 用于保护需要登录才能访问的路由，并集成 onboarding 检查
 * 如果用户未登录，将重定向到v3登录页面
 * 如果用户需要完成 onboarding，将重定向到个性化设置页面
 */
export const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation(['v3/common', 'common']);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [onboardingCheckLoading, setOnboardingCheckLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [inviteCodeCheckLoading, setInviteCodeCheckLoading] = useState(true);
  const [needsInviteCode, setNeedsInviteCode] = useState(false);

  // 统一的加载状态
  const isLoading = authLoading || inviteCodeCheckLoading || onboardingCheckLoading;

  // 获取邀请码检查函数
  const checkInviteCodeVerification = useSetAtom(checkInviteCodeVerificationAtom);

  // 检查邀请码验证状态
  useEffect(() => {
    const checkInviteCodeStatus = async () => {
      console.log('[PrivateRoute] Starting invite code check:', {
        authLoading,
        hasUser: !!user,
        pathname: location.pathname,
      });

      // 如果还在认证加载中，不做任何操作
      if (authLoading) {
        console.log('[PrivateRoute] Still loading auth, skipping invite code check');
        return;
      }

      // 如果没有用户，不做任何操作
      if (!user) {
        console.log('[PrivateRoute] No user, skipping invite code check');
        setInviteCodeCheckLoading(false);
        return;
      }

      // 如果当前路径是邀请码验证页面，跳过检查
      if (location.pathname === '/v3/invite-code') {
        console.log('[PrivateRoute] On invite code page, skipping check');
        setInviteCodeCheckLoading(false);
        return;
      }

      try {
        const needsVerification = await checkInviteCodeVerification(user);
        console.log('[PrivateRoute] Needs invite code verification:', needsVerification);

        setNeedsInviteCode(needsVerification);

        if (needsVerification) {
          console.log('[PrivateRoute] User needs invite code verification, redirecting...');

          // 保存当前路径，以便验证完成后跳转回来
          setInviteCodeRedirectPath(location.pathname + location.search);

          // 重定向到邀请码验证页面
          navigate('/v3/invite-code', { replace: true });
        } else {
          console.log('[PrivateRoute] User does not need invite code verification, proceeding normally');
        }
      } catch (error) {
        console.error('[PrivateRoute] Error checking invite code status:', error);
        // 出错时默认需要验证，确保安全
        setNeedsInviteCode(true);
        setInviteCodeRedirectPath(location.pathname + location.search);
        navigate('/v3/invite-code', { replace: true });
      } finally {
        setInviteCodeCheckLoading(false);
      }
    };

    checkInviteCodeStatus();
  }, [user, authLoading, location.pathname, location.search, navigate, checkInviteCodeVerification]);

  // NOTE: 以后再修复 - 用户反馈经常已经完成了还是错误打开 personal setting 检测
  // 检查 onboarding 状态
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      console.log('[PrivateRoute] Starting onboarding check:', {
        authLoading,
        hasUser: !!user,
        pathname: location.pathname,
        userMetadata: user?.user_metadata,
        isAnonymous: user?.is_anonymous
      });

      // 如果还在认证加载中，不做任何操作
      if (authLoading) {
        console.log('[PrivateRoute] Still loading auth, skipping onboarding check');
        return;
      }

      // 如果还在检查邀请码，不做任何操作
      if (inviteCodeCheckLoading) {
        console.log('[PrivateRoute] Still checking invite code, skipping onboarding check');
        return;
      }

      // 如果没有用户，不做任何操作（让下面的逻辑处理重定向）
      if (!user) {
        console.log('[PrivateRoute] No user, skipping onboarding check');
        setOnboardingCheckLoading(false);
        return;
      }

      // 如果用户需要验证邀请码，不做 onboarding 检查
      if (needsInviteCode) {
        console.log('[PrivateRoute] User needs invite code verification, skipping onboarding check');
        setOnboardingCheckLoading(false);
        return;
      }

      // NOTE: 以后再修复 - 暂时注释掉 onboarding 检测逻辑
      // 如果当前路径应该跳过 onboarding 检查，不做任何操作
      // if (shouldSkipOnboardingCheck(location.pathname)) {
      //   console.log('[PrivateRoute] Path should skip onboarding check:', location.pathname);
      //   setOnboardingCheckLoading(false);
      //   return;
      // }

      // NOTE: 以后再修复 - 暂时注释掉异步检查用户是否需要完成个性化设置
      // try {
      //   const needsOnboardingResult = await needsPersonaOnboarding(user);
      //   console.log('[PrivateRoute] Needs onboarding check result:', needsOnboardingResult);

      //   setNeedsOnboarding(needsOnboardingResult);

      //   if (needsOnboardingResult) {
      //     console.log('[PrivateRoute] User needs persona onboarding, redirecting...');

      //     // 保存当前路径，以便 onboarding 完成后跳转回来
      //     setOnboardingRedirectPath(location.pathname + location.search);

      //     // 重定向到个性化设置页面
      //     navigate('/v3/persona-onboarding', { replace: true });
      //   } else {
      //     console.log('[PrivateRoute] User does not need onboarding, proceeding normally');
      //   }
      // } catch (error) {
      //   console.error('[PrivateRoute] Error checking onboarding status:', error);
      //   // 出错时默认不需要 onboarding，避免无限循环
      //   setNeedsOnboarding(false);
      // } finally {
      //   setOnboardingCheckLoading(false);
      // }

      // NOTE: 以后再修复 - 暂时直接设置为不需要 onboarding
      setNeedsOnboarding(false);
      setOnboardingCheckLoading(false);
    };

    checkOnboardingStatus();
  }, [user, authLoading, inviteCodeCheckLoading, needsInviteCode, location.pathname, location.search, navigate]);

  // 如果正在加载（认证或 onboarding 检查），显示统一的加载指示器
  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-ios-gray6 dark:bg-ios-dark-gray6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-ios-blue dark:text-ios-dark-blue">
            {authLoading ? t('common:loadingText') : t('v3/common:loading.checkingSettings', '正在检查设置...')}
          </div>
        </div>
      </div>
    );
  }

  // 如果用户未登录，重定向到v3登录页面
  if (!user) {
    console.log('[PrivateRoute] No user found, redirecting to /v3/login.');
    return <Navigate to="/v3/login" replace />;
  }

  // 如果用户需要验证邀请码且当前不在邀请码页面，显示加载状态
  if (needsInviteCode && location.pathname !== '/v3/invite-code') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-ios-gray6 dark:bg-ios-dark-gray6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-ios-blue dark:text-ios-dark-blue">正在验证邀请码...</div>
        </div>
      </div>
    );
  }

  // 如果用户需要 onboarding 且当前不在 onboarding 页面，显示加载状态
  if (needsOnboarding && !shouldSkipOnboardingCheck(location.pathname)) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-ios-gray6 dark:bg-ios-dark-gray6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-ios-blue dark:text-ios-dark-blue">{t('v3/common:loading.preparingPersonalization', '正在准备个性化设置...')}</div>
        </div>
      </div>
    );
  }

  // 用户已登录且不需要 onboarding，渲染受保护的内容
  // console.log('[PrivateRoute] User authenticated and onboarding complete, rendering protected content.');
  return (
    <Suspense fallback={
      <div className="w-full h-screen flex items-center justify-center bg-ios-gray6 dark:bg-ios-dark-gray6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-ios-blue dark:text-ios-dark-blue">{t('v3/common:loading.loadingContent', '加载内容中...')}</div>
        </div>
      </div>
    }>
      {children}
    </Suspense>
  );
};

export default PrivateRoute;
